// src/components/contracts/ContractsPanel.jsx
// The Contracts panel on a WON proposal: what has been sent, and the flow to
// send another. Each contract is its OWN DocuSign envelope — the SOW and an
// ancillary agreement are signed independently, never bundled into one signing
// session — so this list is n records, not one.
//
// Marking a proposal won does NOT send anything. Sending is always an explicit
// action here, at whatever time Craig chooses.
import { useState, useEffect } from "react";
import {
  contractsForProposal, templatesForProposal, initialFieldValues,
  validateSend, recipientFrom, makeContractInstance, CONTRACT_STATUS_META,
  fieldsByFilledBy, computePricingPreview,
  /* Signed without DocuSign — the pipeline's gate used to have DocuSign as its
     only input. These record a signature captured elsewhere; contractModel.js
     carries the rules (the date is the Autotask start date, refresh must not
     clobber a manual mark, nothing here writes an onboarding record). */
  signedSourceOf, canRefreshStatus, canMarkSigned, canUnmarkSigned, todayInputValue,
  validateSignedDate, markSignedPatch, unmarkSignedPatch, makeSignedElsewhereInstance,
} from "./contractModel";
import { sendContract, getContractStatus, getContractsConfig } from "../../api/contractsApi";
import ItemListEditor from "./ItemListEditor";
import OnboardingPanel from "../onboarding/OnboardingPanel";
import { hasSignedContract, onboardingRecordOf } from "../onboarding/onboardingRecord";
import { StepCard } from "../onboarding/StepCard";
import { stepById, stepState, toggleSkippedPatch, skippedAt } from "../onboarding/postWonSteps";

const btn = (primary) => ({
  padding: "6px 11px", borderRadius: 7, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
  border: primary ? "none" : "1px solid var(--border,#d0d0d0)",
  background: primary ? "var(--dt-secondary,#0b80a5)" : "var(--card,#fff)",
  color: primary ? "#fff" : "var(--text,#222)",
});
const inp = {
  width: "100%", padding: "6px 8px", borderRadius: 6, fontSize: 12,
  border: "1px solid var(--border,#d0d0d0)", background: "var(--card,#fff)",
  color: "var(--text,#222)", fontFamily: "inherit",
};
const lbl = { display: "block", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: "var(--text3,#888)", marginBottom: 2 };

function StatusBadge({ status }) {
  const m = CONTRACT_STATUS_META[status] || CONTRACT_STATUS_META.draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 5,
      background: m.bg, color: m.fg, whiteSpace: "nowrap" }}>{m.label}</span>
  );
}

const fmtWhen = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/* The signature-date form, shared by "Mark as signed" on a row and "Record
   signed contract" in the picker. Date required (defaults to today, editable,
   never written unseen); reference optional; one line saying what this is. */
function SignForm({ form, setForm, check, onCheck, onSave, onCancel, label, testId }) {
  return (
    <div data-testid={testId} style={{ marginTop: 8, padding: 9, borderRadius: 7, border: "1px dashed var(--border,#ccc)", background: "var(--s2,#f7f9fb)" }}>
      <div style={{ fontSize: 10.5, color: "var(--text3,#888)", marginBottom: 6 }}>
        Records a signature captured elsewhere — another contract system, or on paper. The date becomes the
        contract start date in Autotask, so make sure it is the day it was actually signed.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 160px) minmax(0, 1fr)", gap: 8 }}>
        <div>
          <label style={lbl}>Signed on *</label>
          <input style={inp} type="date" data-testid="sign-date" value={form.date} max={todayInputValue()}
                 onChange={(e) => { setForm((f) => ({ ...f, date: e.target.value })); onCheck(e.target.value); }} />
        </div>
        <div>
          <label style={lbl}>Reference (optional)</label>
          <input style={inp} type="text" data-testid="sign-ref" value={form.ref} placeholder="e.g. the other system's document number"
                 onChange={(e) => setForm((f) => ({ ...f, ref: e.target.value }))} />
        </div>
      </div>
      {check.error && <div data-testid="sign-error" style={{ fontSize: 10.5, color: "#c0392b", marginTop: 5 }}>{check.error}</div>}
      {!check.error && check.warning && <div data-testid="sign-warning" style={{ fontSize: 10.5, color: "#8a5a12", marginTop: 5 }}>⚠ {check.warning}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button style={btn(true)} data-testid="sign-save" onClick={onSave}>{label}</button>
        <button style={btn(false)} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function ContractsPanel({
  proposal, contractTemplates = [], contractInstances = [], setContractInstances,
  saveSnapshot, flash,
  // The onboarding record lives on the PROPOSAL, so setting it up needs a way
  // to write there — see onboardingRecord.js for why it is not on the instance.
  patchProposal,
  // Onboarding needs the quotes array, because Stage D writes into it — quotes
  // live in the state blob and must go through saveSnapshot like every other
  // quote write, not through a backend route that would race the SPA's save.
  quotes = [], setQuotes,
}) {
  const [picking, setPicking] = useState(false);
  /* "send" = the DocuSign path. "record" = the same template picker and field
     form, but the instance is created ALREADY SIGNED — for a contract that
     never went through the app (a third-party contract system). */
  const [mode, setMode] = useState("send");
  const [selectedId, setSelectedId] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(null);
  const [dsConfig, setDsConfig] = useState(null);
  /* The signature-date form: { id } for marking an existing row, or "new"
     inside the record picker. The date is asked for every time — see
     validateSignedDate for why it is never defaulted silently. */
  const [marking, setMarking] = useState(null);
  const [signForm, setSignForm] = useState({ date: todayInputValue(), ref: "" });
  const [signCheck, setSignCheck] = useState({ error: null, warning: null });

  useEffect(() => {
    if (!picking || mode !== "send" || dsConfig) return;
    getContractsConfig().then(setDsConfig).catch(() => setDsConfig({ configured: false }));
  }, [picking, mode, dsConfig]);

  const checkDate = (date) => {
    const r = validateSignedDate(date, { proposalCreatedAt: proposal?.createdAt ?? null });
    setSignCheck({ error: r.error, warning: r.warning });
    return r;
  };
  const openSignForm = (target) => {
    setMarking(target);
    setSignForm({ date: todayInputValue(), ref: "" });
    setSignCheck({ error: null, warning: null });
  };
  const closeSignForm = () => { setMarking(null); setSignCheck({ error: null, warning: null }); };

  const mine = contractsForProposal(contractInstances, proposal?.id);
  const offered = templatesForProposal(contractTemplates, proposal?.templateKey);
  const selected = offered.find((t) => t.id === selectedId) || null;
  const content = proposal?.content || {};
  const groups = fieldsByFilledBy(selected);
  const pricing = selected ? computePricingPreview(content, values.discount, selected.documentType) : null;

  const commit = (updater) => setContractInstances((prev) => {
    const next = typeof updater === "function" ? updater(prev) : updater;
    saveSnapshot({ contractInstances: next });
    return next;
  });

  const choose = (t) => {
    setSelectedId(t.id);
    setValues(initialFieldValues(t, content));
  };

  const reset = () => { setPicking(false); setMode("send"); setSelectedId(null); setValues({}); closeSignForm(); };

  const openPicker = (which) => { setMode(which); setPicking(true); if (which === "record") openSignForm("new"); };

  /* (a) Mark an EXISTING instance as signed elsewhere. Sets status, the entered
     date and provenance on the instance and nothing else. Marking creates
     nothing in Autotask — it only reveals the onboarding panel, and every stage
     behind it is its own explicit click with its own preview — so the form's
     Save is the whole confirmation. */
  const saveMark = (ci) => {
    const r = checkDate(signForm.date);
    if (r.error) return;
    const patch = markSignedPatch({ signedAtIso: r.iso, externalRef: signForm.ref });
    commit((prev) => prev.map((x) => (x.id === ci.id ? { ...x, ...patch } : x)));
    flash(`Recorded as signed on ${new Date(r.iso).toLocaleDateString("en-AU")}.`);
    closeSignForm();
  };

  /* Reverse a manual mark. ⚠ This hides the panel; it does not undo anything the
     pipeline already created — the onboarding record lives on the proposal. The
     confirm says so whenever such a record exists. */
  const unmark = (ci) => {
    const rec = onboardingRecordOf(proposal, contractInstances);
    const msg = rec
      ? "Un-mark this contract as signed?\n\nThis only clears the signature on this record. Anything the onboarding pipeline already created — the Autotask contract, tickets, quotes — stays created; re-marking shows the panel again with those already done."
      : "Un-mark this contract as signed? The signature date and reference on this record will be cleared.";
    if (!window.confirm(msg)) return;
    commit((prev) => prev.map((x) => (x.id === ci.id ? { ...x, ...unmarkSignedPatch(x) } : x)));
    flash("Signature cleared.");
  };

  /* (b) Record a contract that never went through the app at all — created
     already signed, never "sent" from here. This is the path a third-party
     contract system actually needs: without it there is nothing to mark. */
  const doRecord = () => {
    if (!selected || busy) return;
    const r = checkDate(signForm.date);
    if (r.error) return;
    const inst = makeSignedElsewhereInstance({
      proposalId: proposal.id, template: selected, fieldValues: values,
      signedAtIso: r.iso, externalRef: signForm.ref,
    });
    commit((prev) => [...prev, inst]);
    flash(`Recorded "${selected.name}" as signed on ${new Date(r.iso).toLocaleDateString("en-AU")}.`);
    reset();
  };

  const doSend = async () => {
    if (!selected || busy) return;
    const problems = validateSend(selected, values);
    const { email, name } = recipientFrom(selected, values, content);
    if (!email) problems.push("No recipient email — set the contact email on the proposal, or fill the email field above.");
    if (problems.length) return flash(problems.join(" "), "err");

    setBusy(true);
    // The instance is created ONLY after DocuSign accepts. A record written
    // first would leave a phantom "draft" contract behind every failed send,
    // and there is no way to tell those from ones deliberately left unsent.
    try {
      const r = await sendContract({
        templateName: selected.name,
        content: selected.content,
        fieldSchema: selected.fieldSchema,
        documentType: selected.documentType,
        // The backend recomputes pricing from this rather than trusting figures
        // posted by the browser.
        proposalContent: content,
        fieldValues: values,
        recipientEmail: email,
        recipientName: name || email,
        proposalId: proposal.id,
        contractTemplateId: selected.id,
      });
      const now = Date.now();
      const inst = {
        ...makeContractInstance({ proposalId: proposal.id, contractTemplateId: selected.id, fieldValues: values }),
        templateNameAtSend: selected.name,
        docuSignEnvelopeId: r.envelopeId,
        status: r.status || "sent",
        sentAt: now,
        updatedAt: now,
      };
      commit((prev) => [...prev, inst]);
      flash(`Sent to ${email} for signature.`);
      reset();
    } catch (e) {
      const d = e?.response?.data;
      if (d?.code === "consent_required" && d.consentUrl) {
        flash("DocuSign needs one-time consent — see the link below.", "err");
        setDsConfig((c) => ({ ...(c || {}), configured: true, consentUrl: d.consentUrl, needsConsent: true }));
      } else {
        flash(d?.error || e.message || "Send failed", "err");
      }
    } finally { setBusy(false); }
  };

  const refresh = async (ci) => {
    if (!ci.docuSignEnvelopeId || checking) return;
    setChecking(ci.id);
    try {
      const r = await getContractStatus(ci.docuSignEnvelopeId);
      commit((prev) => prev.map((x) => (x.id === ci.id ? {
        ...x,
        status: r.status || x.status,
        signedAt: r.signedAt ? new Date(r.signedAt).getTime() : x.signedAt,
        lastStatusCheckAt: Date.now(),
        lastError: null,
        updatedAt: Date.now(),
      } : x)));
      flash(`Status: ${CONTRACT_STATUS_META[r.status]?.label || r.status}`);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || "Status check failed";
      commit((prev) => prev.map((x) => (x.id === ci.id ? { ...x, lastError: msg, lastStatusCheckAt: Date.now() } : x)));
      flash(msg, "err");
    } finally { setChecking(null); }
  };

  /* ── Post Won Proposal Steps ──────────────────────────────────────────────
     Step A — the client's contract — is this panel: derived from the contract
     instances (sent / delivered / signed = done), never stored. Steps B–E are
     the OnboardingPanel below, unlocked once a contract is signed or Step A is
     marked nothing-to-do. The display letters come from postWonSteps.js; the
     backend's own stage letters are untouched. */
  const stepA = stepById("client_contract");
  const onboardingRec = onboardingRecordOf(proposal, contractInstances);
  const stateA = stepState("client_contract", { proposal, contractInstances, record: onboardingRec });
  const stepsUnlocked = hasSignedContract(contractInstances, proposal?.id) || !!skippedAt(onboardingRec, "client_contract");

  return (
    <div style={{ borderTop: "1px solid var(--border,#eee)", paddingTop: 12, marginTop: 12 }} data-testid="post-won-steps">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Post Won Proposal Steps</div>
        <span style={{ fontSize: 11, color: "var(--text3,#999)" }}>
          A → E, in order. Each step shows what it created; tick “nothing to do” where a step does not apply.
        </span>
      </div>

      <StepCard step={stepA} state={stateA}
        onToggleSkip={patchProposal ? () => patchProposal(toggleSkippedPatch(onboardingRec, "client_contract")) : undefined}
        actions={!picking && (
          <>
            <button style={btn(true)} onClick={() => openPicker("send")}>+ Send Contract</button>
            <button style={btn(false)} data-testid="record-signed-contract"
                    title="For a contract signed outside the app — another contract system, or on paper. Creates the record already signed; nothing is sent."
                    onClick={() => openPicker("record")}>Record signed contract</button>
          </>
        )}>
      <div style={{ fontSize: 10.5, color: "var(--text3,#999)", margin: "6px 0 4px" }}>
        {mine.length ? `${mine.length} on file` : "none yet"} · each is its own agreement
      </div>

      {mine.map((ci) => {
        const tpl = contractTemplates.find((t) => t.id === ci.contractTemplateId);
        const source = signedSourceOf(ci);
        const isManual = source === "manual";
        return (
          <div key={ci.id} data-testid={`contract-row-${ci.id}`} style={{ border: "1px solid var(--border,#eee)", borderRadius: 8, padding: 9, marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* The name it was SENT under, not the template's current name —
                  a template can be renamed after the fact. */}
              <div style={{ fontSize: 12, fontWeight: 600 }}>{ci.templateNameAtSend || tpl?.name || "Contract"}</div>
              <StatusBadge status={ci.status} />
              {/* A manually-signed instance reads as such — Craig will be looking
                  at these months later trying to remember where the signature lives. */}
              {isManual && ci.status === "signed" && (
                <span data-testid="recorded-manually" title="Signature recorded by hand, not via DocuSign"
                      style={{ fontSize: 10, color: "var(--text3,#888)", fontStyle: "italic" }}>
                  recorded manually{ci.externalRef ? ` · ref ${ci.externalRef}` : ""}
                </span>
              )}
              <span style={{ fontSize: 10.5, color: "var(--text3,#999)" }}>
                {ci.sentAt ? `sent ${fmtWhen(new Date(ci.sentAt).toISOString())}` : ""}
                {ci.signedAt ? ` · signed ${fmtWhen(new Date(ci.signedAt).toISOString())}` : ""}
              </span>
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {canMarkSigned(ci) && (
                  <button style={btn(false)} data-testid={`mark-signed-${ci.id}`}
                          title="Record that this contract was signed outside DocuSign"
                          onClick={() => (marking?.id === ci.id ? closeSignForm() : openSignForm({ id: ci.id }))}>
                    Mark as signed
                  </button>
                )}
                {canUnmarkSigned(ci) && (
                  <button style={btn(false)} data-testid={`unmark-signed-${ci.id}`}
                          title="Clear the manual signature on this record" onClick={() => unmark(ci)}>
                    Un-mark
                  </button>
                )}
                {/* ⚠ Refresh asks DocuSign about an envelope. With no envelope, or
                    a manual signature, it has nothing to ask and would only
                    overwrite what DocuSign never saw — so it is disabled, and
                    the title says why. */}
                <button style={btn(false)} data-testid={`refresh-${ci.id}`}
                        disabled={checking === ci.id || !canRefreshStatus(ci)}
                        title={!ci.docuSignEnvelopeId ? "No DocuSign envelope to refresh — this contract was not sent from here"
                          : isManual ? "Signed outside DocuSign — refreshing would overwrite the recorded signature"
                          : "Ask DocuSign for the envelope's current status"}
                        onClick={() => refresh(ci)}>
                  {checking === ci.id ? "Checking…" : "↻ Refresh status"}
                </button>
              </span>
            </div>
            <div style={{ fontSize: 10, color: "var(--text3,#aaa)", marginTop: 3 }}>
              {ci.fieldValues?.clientContactEmail || recipientFrom(tpl, ci.fieldValues, content).email || "—"}
              {ci.lastStatusCheckAt ? ` · checked ${fmtWhen(new Date(ci.lastStatusCheckAt).toISOString())}` : ""}
            </div>
            {ci.lastError && (
              <div style={{ fontSize: 10.5, color: "#c0392b", marginTop: 3 }}>{ci.lastError}</div>
            )}
            {marking?.id === ci.id && (
              <SignForm form={signForm} setForm={setSignForm} check={signCheck} onCheck={checkDate}
                        onSave={() => saveMark(ci)} onCancel={closeSignForm} label="Save" testId={`sign-form-${ci.id}`} />
            )}
          </div>
        );
      })}

      {/* ⚠ ONE panel per PROPOSAL, not one per signed envelope.
          Reaching `signed` on ANY contract reveals the pipeline; nothing fires
          on its own. Gated on the DocuSign status rather than on the proposal
          being marked won — those are separate trigger points, and a proposal
          can be won without a contract being signed.

          This used to render inside the loop above, once per signed instance,
          with the onboarding record stored on that instance. A proposal with
          two signed envelopes therefore got two panels holding two empty
          records, and running the pipeline from each created a SECOND Autotask
          contract and a duplicate set of tickets. See onboardingRecord.js. */}
      {picking && (
        <div style={{ border: "1px solid var(--border,#ddd)", borderRadius: 8, padding: 10, marginTop: 6 }}>
          {mode === "record" && (
            <div style={{ padding: "7px 10px", borderRadius: 7, background: "var(--s2,#f0f4f7)", color: "var(--text2,#444)", fontSize: 11, marginBottom: 8 }}>
              <strong>Recording a signed contract.</strong> Nothing is sent — this records a signature captured
              elsewhere so the onboarding pipeline can run against it.
            </div>
          )}
          {mode === "send" && dsConfig && !dsConfig.configured && (
            <div style={{ padding: "7px 10px", borderRadius: 7, background: "#fff4e2", color: "#8a5a12", fontSize: 11, fontWeight: 600, marginBottom: 8 }}>
              ⚠ DocuSign is not configured — sending will fail until the DOCUSIGN-* secrets are in Key Vault.
            </div>
          )}
          {mode === "send" && dsConfig?.needsConsent && (
            <div style={{ padding: "7px 10px", borderRadius: 7, background: "#fff4e2", color: "#8a5a12", fontSize: 11, marginBottom: 8 }}>
              One-time consent required —{" "}
              <a href={dsConfig.consentUrl} target="_blank" rel="noopener noreferrer">open this once</a>, approve, then send again.
            </div>
          )}

          {!selected && (
            <>
              <div style={{ fontSize: 11, color: "var(--text3,#888)", marginBottom: 6 }}>
                Which contract? Templates for this proposal's tier are listed first; ancillary
                contracts are available on any proposal.
              </div>
              {offered.map((t) => {
                const tiers = Array.isArray(t.appliesToTiers) ? t.appliesToTiers : [];
                const matches = proposal?.templateKey && tiers.includes(proposal.templateKey);
                return (
                  <button key={t.id} style={{ ...btn(false), width: "100%", textAlign: "left", marginBottom: 5,
                                              borderColor: matches ? "var(--dt-secondary,#0b80a5)" : undefined }}
                          onClick={() => choose(t)}>
                    {t.name}
                    {matches && <span style={{ color: "var(--dt-secondary,#0b80a5)", fontWeight: 700 }}> · matches this tier</span>}
                    {!tiers.length && <span style={{ color: "var(--text3,#999)" }}> · ancillary</span>}
                  </button>
                );
              })}
              {!offered.length && (
                <div style={{ fontSize: 11.5, color: "#c0392b" }}>
                  No active contract templates. Open <strong>Contract Templates</strong>, load the
                  seeded legal text into one and make it active.
                </div>
              )}
              <button style={{ ...btn(false), marginTop: 4 }} onClick={reset}>Cancel</button>
            </>
          )}

          {selected && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700 }}>{selected.name}</div>
                <button style={{ ...btn(false), marginLeft: "auto" }} onClick={() => setSelectedId(null)}>← Back</button>
              </div>
              {/* SENDER fields only. Computed pricing is read-only below, and
                  recipient-filled fields are absent entirely — the customer's
                  cyber-insurance figure and their own signatory details are
                  theirs to enter during signing, not ours to guess. */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 }}>
                {groups.sender.filter((f) => f.type !== "itemList").map((f) => (
                  <div key={f.key}>
                    <label style={lbl}>
                      {f.label || f.key}{f.required ? " *" : ""}
                      {f.autoFillFrom && <span style={{ color: "var(--text3,#bbb)", fontWeight: 500 }}> · from proposal</span>}
                    </label>
                    <input
                      style={inp}
                      type={f.type === "date" ? "date" : f.type === "email" ? "email" : "text"}
                      inputMode={f.type === "currency" || f.type === "number" ? "decimal" : undefined}
                      value={values[f.key] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>

              {/* Appendix 3 — Included Hardware. Rendered separately from the
                  text fields above because each holds an ARRAY, and because a
                  category left empty is omitted from the contract entirely
                  rather than printing an empty heading. */}
              {groups.sender.filter((f) => f.type === "itemList").length > 0 && (
                <div style={{ marginTop: 12, border: "1px solid var(--border,#eee)", borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ ...lbl, marginBottom: 8 }}>Appendix 3 — Included Hardware</div>
                  {groups.sender.filter((f) => f.type === "itemList").map((f) => (
                    <ItemListEditor
                      key={f.key}
                      label={f.label || f.key}
                      hasQuantity={f.hasQuantity !== false}
                      items={values[f.key]}
                      onChange={(next) => setValues((v) => ({ ...v, [f.key]: next }))}
                    />
                  ))}
                  <div style={{ fontSize: 10, color: "var(--text3,#999)" }}>
                    Appendix 1 refers to these as the “Specified Devices”. Leave a category empty and it
                    is left out of the contract; leave all four empty and Appendix 3 is omitted entirely.
                  </div>
                </div>
              )}

              {/* Computed from the won proposal — shown so the figures can be
                  checked before sending, but not editable. The Discount field
                  above is the only lever on the outcome. */}
              {groups.computed.length > 0 && pricing && (
                <div style={{ marginTop: 10, border: "1px solid var(--border,#eee)", borderRadius: 8, padding: 9 }}>
                  <div style={{ ...lbl, marginBottom: 5 }}>Pricing — computed from this proposal</div>
                  <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
                    <tbody>
                      {pricing.userCount > 0 && (
                        <tr><td>Users</td><td style={{ textAlign: "right" }}>{pricing.userCount} × ${pricing.userPrice.toFixed(2)}</td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>${pricing.userSubtotal.toFixed(2)}</td></tr>
                      )}
                      <tr><td>Devices</td><td style={{ textAlign: "right" }}>{pricing.deviceCount} × ${pricing.devicePrice.toFixed(2)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>${pricing.deviceSubtotal.toFixed(2)}</td></tr>
                      {pricing.onboardingSubtotal > 0 && (
                        <tr><td>Device onboarding (one-off)</td><td style={{ textAlign: "right" }}>{pricing.deviceCount} × ${pricing.onboardingRate.toFixed(2)}</td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>${pricing.onboardingSubtotal.toFixed(2)}</td></tr>
                      )}
                      <tr><td colSpan={2} style={{ textAlign: "right" }}>Subtotal</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>${pricing.grandSubtotal.toFixed(2)}</td></tr>
                      {pricing.discount > 0 && (
                        <tr><td colSpan={2} style={{ textAlign: "right" }}>Discount</td>
                            <td style={{ textAlign: "right" }}>−${pricing.discount.toFixed(2)}</td></tr>
                      )}
                      <tr><td colSpan={2} style={{ textAlign: "right" }}>Tax (GST 10%)</td>
                          <td style={{ textAlign: "right" }}>${pricing.tax.toFixed(2)}</td></tr>
                      <tr><td colSpan={2} style={{ textAlign: "right", fontWeight: 700 }}>Total</td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>${pricing.total.toFixed(2)}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {mode === "send" && groups.recipient.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 10.5, color: "var(--text3,#999)" }}>
                  The customer fills these during signing:{" "}
                  {groups.recipient.map((f) => f.label || f.key).join(", ")}.
                </div>
              )}
              {mode === "send" ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
                  <button style={btn(true)} disabled={busy} onClick={doSend}>
                    {busy ? "Sending…" : "Send for Signature"}
                  </button>
                  <button style={btn(false)} disabled={busy} onClick={reset}>Cancel</button>
                  <span style={{ fontSize: 10.5, color: "var(--text3,#999)" }}>
                    Sends its own DocuSign envelope, separate from any other contract on this proposal.
                  </span>
                </div>
              ) : (
                <SignForm form={signForm} setForm={setSignForm} check={signCheck} onCheck={checkDate}
                          onSave={doRecord} onCancel={reset} label="Record as signed" testId="sign-form-new" />
              )}
            </>
          )}
        </div>
      )}
      </StepCard>

      {/* Steps B–E. ONE panel per PROPOSAL, not one per signed envelope — see
          onboardingRecord.js for the duplicate this prevents. Unlocked by a
          signature on ANY contract, or by Step A being marked nothing-to-do. */}
      {stepsUnlocked && setQuotes ? (
        <OnboardingPanel
          proposal={proposal}
          contractInstances={contractInstances}
          patchProposal={patchProposal}
          saveSnapshot={saveSnapshot}
          quotes={quotes}
          setQuotes={setQuotes}
          flash={flash}
        />
      ) : (
        <div data-testid="steps-locked" style={{ fontSize: 10.5, color: "var(--text3,#999)", marginTop: 8, fontStyle: "italic" }}>
          Stages B–E unlock once a contract is signed — or tick “nothing to do” on Stage A if no contract applies.
        </div>
      )}
    </div>
  );
}
